// Utility functions for validating media file uploads

export type MediaType = 'image' | 'video';

export interface MediaValidationResult {
  valid: boolean;
  error?: string;
}

const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime']; // quicktime = mov
const MAX_IMAGE_SIZE_MB = 5;
const MAX_VIDEO_SIZE_MB = 100;
const MAX_VIDEO_DURATION_SEC = 300; // 5 minutes

export function validateMediaFile(file: File, type: MediaType, videoDuration?: number): MediaValidationResult {
  if (type === 'image') {
    if (!IMAGE_TYPES.includes(file.type)) {
      return { valid: false, error: 'Only JPEG, PNG, or WEBP images are allowed.' };
    }
    if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
      return { valid: false, error: `Image size must be less than ${MAX_IMAGE_SIZE_MB}MB.` };
    }
    return { valid: true };
  } else if (type === 'video') {
    if (!VIDEO_TYPES.includes(file.type)) {
      return { valid: false, error: 'Only MP4, WEBM, or MOV videos are allowed.' };
    }
    if (file.size > MAX_VIDEO_SIZE_MB * 1024 * 1024) {
      return { valid: false, error: `Video size must be less than ${MAX_VIDEO_SIZE_MB}MB.` };
    }
    if (typeof videoDuration === 'number' && videoDuration > MAX_VIDEO_DURATION_SEC) {
      return { valid: false, error: `Video length must be less than 5 minutes.` };
    }
    return { valid: true };
  }
  return { valid: false, error: 'Unsupported media type.' };
}

// Helper to get video duration (returns a Promise)
export function getVideoDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.src = url;
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(video.duration);
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load video metadata.'));
    };
  });
}
